<!-- src/views/treatment-plans/TreatmentPlanForm.vue -->
<template>
    <div class="treatment-plan-form">
      <!-- Back button and page title -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <router-link :to="returnPath" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver
        </router-link>
        <h1>{{ isEditMode ? 'Editar Plan de Tratamiento' : 'Nuevo Plan de Tratamiento' }}</h1>
      </div>
  
      <!-- Loading state -->
      <div v-if="loading" class="d-flex justify-content-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
  
      <!-- Form content -->
      <div v-else class="row">
        <div class="col-md-8 mx-auto">
          <div class="card">
            <div class="card-body">
              <form @submit.prevent="submitForm">
                <!-- Alert for errors -->
                <div v-if="error" class="alert alert-danger mb-4">
                  <i class="bi bi-exclamation-triangle-fill me-2"></i>
                  {{ error }}
                </div>
  
                <div class="row g-3">
                  <!-- Patient selection -->
                  <div class="col-12 mb-3">
                    <label for="patient" class="form-label">Paciente *</label>
                    <div v-if="preSelectedPatientId" class="input-group">
                      <span class="form-control">{{ getPatientName(preSelectedPatientId) }}</span>
                      <button class="btn btn-outline-secondary" type="button" @click="clearPatient">
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                    <select v-else id="patient" class="form-select" v-model="form.patient" required>
                      <option value="" disabled>Seleccionar paciente</option>
                      <option v-for="patient in patients" :key="patient.id" :value="patient.id">
                        {{ patient.first_name }} {{ patient.last_name }}
                      </option>
                    </select>
                    <div v-if="validationErrors.patient" class="text-danger mt-1 small">
                      {{ validationErrors.patient }}
                    </div>
                  </div>
  
                  <!-- Diagnostic -->
                  <div class="col-12 mb-3">
                    <label for="diagnostic" class="form-label">Diagnóstico</label>
                    <textarea id="diagnostic" class="form-control" v-model="form.diagnostic" 
                      rows="3" placeholder="Diagnóstico del paciente..."></textarea>
                  </div>
  
                  <!-- Treatment Plan -->
                  <div class="col-12 mb-3">
                    <label for="treatment_plan" class="form-label">Plan de Tratamiento</label>
                    <textarea id="treatment_plan" class="form-control" v-model="form.treatment_plan" 
                      rows="5" placeholder="Detalles del plan de tratamiento..."></textarea>
                  </div>
  
                  <!-- Observations -->
                  <div class="col-12 mb-3">
                    <label for="observations" class="form-label">Observaciones</label>
                    <textarea id="observations" class="form-control" v-model="form.observations" 
                      rows="3" placeholder="Observaciones adicionales..."></textarea>
                  </div>
  
                  <!-- Form actions -->
                  <div class="col-12 mt-4">
                    <div class="d-flex justify-content-end">
                      <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm">
                        Cancelar
                      </button>
                      <button type="submit" class="btn btn-primary" :disabled="submitting">
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"
                          aria-hidden="true"></span>
                        {{ isEditMode ? 'Guardar cambios' : 'Guardar plan' }}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
  
              <!-- Delete plan button (Edit mode only) -->
              <div v-if="isEditMode" class="mt-4 pt-4 border-top">
                <h5 class="text-danger mb-3">Zona de peligro</h5>
                <p class="text-muted mb-3">
                  Al eliminar el plan de tratamiento, se borrarán todos los datos asociados.
                  Esta acción no se puede deshacer.
                </p>
                <button type="button" class="btn btn-outline-danger" @click="showDeleteConfirmation = true">
                  <i class="bi bi-trash me-2"></i>Eliminar plan
                </button>
  
                <!-- Delete confirmation modal -->
                <div class="modal fade" :class="{ show: showDeleteConfirmation, 'd-block': showDeleteConfirmation }"
                  tabindex="-1" role="dialog" aria-hidden="true">
                  <div class="modal-dialog">
                    <div class="modal-content">
                      <div class="modal-header">
                        <h5 class="modal-title">Confirmar eliminación</h5>
                        <button type="button" class="btn-close" @click="showDeleteConfirmation = false"
                          aria-label="Close"></button>
                      </div>
                      <div class="modal-body">
                        <p>¿Estás seguro que deseas eliminar este plan de tratamiento?</p>
                        <p class="text-danger">Esta acción no se puede deshacer.</p>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-secondary"
                          @click="showDeleteConfirmation = false">Cancelar</button>
                        <button type="button" class="btn btn-danger" @click="deletePlan" :disabled="deleting">
                          <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"
                            aria-hidden="true"></span>
                          Eliminar plan
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Modal backdrop for delete confirmation -->
      <div v-if="showDeleteConfirmation" class="modal-backdrop fade show"></div>
    </div>
  </template>
  
  <script>
import { patientService, treatmentPlanService } from '@/services/api';

export default {
  name: 'TreatmentPlanForm',
  props: {
    id: {
      type: [String, Number],
      required: false
    }
  },
  data() {
    return {
      form: {
        patient: '',
        diagnostic: '',
        treatment_plan: '',
        observations: ''
      },
      originalForm: {},
      patients: [],
      preSelectedPatientId: null,
      validationErrors: {},
      loading: true,
      submitting: false,
      deleting: false,
      error: null,
      showDeleteConfirmation: false
    };
  },
  computed: {
    isEditMode() {
      return !!this.id;
    },
    planId() {
      return this.id;
    },
    returnPath() {
      if (this.isEditMode) {
        return `/treatment-plans/${this.planId}`;
      }

      // If there's a pre-selected patient, return to patient detail
      if (this.preSelectedPatientId) {
        return `/patients/${this.preSelectedPatientId}`;
      }

      // Otherwise, return to treatment plans list
      return '/treatment-plans';
    }
  },
  async created() {
    try {
      // Load all patients
      const patientsResponse = await patientService.getAll();
      this.patients = patientsResponse.data;

      // Check if a patient ID was provided in the query params
      const patientId = this.$route.query.patient;
      if (patientId) {
        this.preSelectedPatientId = parseInt(patientId);
        this.form.patient = this.preSelectedPatientId;
      }

      if (this.isEditMode) {
        await this.fetchPlan();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'No se pudieron cargar los datos necesarios. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchPlan() {
      try {
        // Directly fetch the treatment plan
        const response = await treatmentPlanService.get(this.planId);
        const plan = response.data;
        
        if (!plan) {
          this.error = 'Plan de tratamiento no encontrado';
          return;
        }
        
        // Set form data
        this.form = {
          patient: plan.patient,
          diagnostic: plan.diagnostic || '',
          treatment_plan: plan.treatment_plan || '',
          observations: plan.observations || ''
        };
        
        this.originalForm = { ...this.form };
        this.preSelectedPatientId = plan.patient;
      } catch (error) {
        console.error('Error fetching treatment plan:', error);
        this.error = 'No se pudo cargar la información del plan de tratamiento.';
      }
    },
    
    validateForm() {
      this.validationErrors = {};
      let isValid = true;

      // Patient validation
      if (!this.form.patient) {
        this.validationErrors.patient = 'Debes seleccionar un paciente';
        isValid = false;
      }

      return isValid;
    },

    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.submitting = true;
      this.error = null;

      try {
        // Create a copy of the form data
        const formData = {
          patient: this.form.patient,
          diagnostic: this.form.diagnostic,
          treatment_plan: this.form.treatment_plan,
          observations: this.form.observations
        };
        
        if (this.isEditMode) {
          // Update existing plan
          await treatmentPlanService.update(this.planId, formData);
          this.$router.push(`/treatment-plans/${this.planId}`);
        } else {
          // Create new treatment plan
          const response = await treatmentPlanService.create(formData);
          this.$router.push(`/treatment-plans/${response.data.id}`);
        }
      } catch (error) {
        console.error('Error saving treatment plan:', error);
        this.error = 'Error al guardar el plan de tratamiento. Por favor, intenta de nuevo.';
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      if (this.isEditMode) {
        // In edit mode, reset to original values
        this.form = { ...this.originalForm };
      } else {
        // In create mode, clear the form but keep the pre-selected patient
        this.form = {
          patient: this.preSelectedPatientId || '',
          diagnostic: '',
          treatment_plan: '',
          observations: ''
        };
      }

      this.validationErrors = {};
      this.error = null;
    },

    async deletePlan() {
      this.deleting = true;
      try {
        await treatmentPlanService.delete(this.planId);
        this.showDeleteConfirmation = false;
        // Redirect to treatment plans list
        this.$router.push('/treatment-plans');
      } catch (error) {
        console.error('Error deleting treatment plan:', error);
        this.error = 'No se pudo eliminar el plan de tratamiento. Por favor, intenta de nuevo.';
        this.showDeleteConfirmation = false;
      } finally {
        this.deleting = false;
      }
    },

    clearPatient() {
      this.preSelectedPatientId = null;
      this.form.patient = '';
    },

    getPatientName(patientId) {
      const patient = this.patients.find(p => p.id === parseInt(patientId));
      return patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente desconocido';
    }
  }
};
  </script>
  
  <style scoped>
  .modal {
    background-color: rgba(0, 0, 0, 0.5);
  }
  </style>